import { __rest } from 'tslib';
import React__default from 'react';
import classNames from 'classnames';
import RowContext from './RowContext.es.js';
import ResponsiveObserve, { responsiveArray } from './util.es.js';
import { BREAK_POINTS } from './constant.es.js';
import { CommonStyles } from '../BaseProps/index.es.js';
import styled from '@emotion/styled';

const Row = React__default.forwardRef((props, ref) => {
    const { justify, align, className, style, children, gutter = 0, wrap } = props, others = __rest(props, ["justify", "align", "className", "style", "children", "gutter", "wrap"]);
    const [screens, setScreens] = React__default.useState({
        xs: true,
        sm: true,
        md: true,
        lg: true,
        xl: true,
        xxl: true,
    });
    const gutterRef = React__default.useRef(gutter);
    React__default.useEffect(() => {
        const token = ResponsiveObserve.subscribe((screen) => {
            const currentGutter = gutterRef.current || 0;
            if ((!Array.isArray(currentGutter) &&
                typeof currentGutter === 'object') ||
                (Array.isArray(currentGutter) &&
                    (typeof currentGutter[0] === 'object' ||
                        typeof currentGutter[1] === 'object'))) {
                setScreens(screen);
            }
        });
        return () => ResponsiveObserve.unsubscribe(token);
    }, []);
    const getGutter = () => {
        const results = [0, 0];
        const normalizedGutter = Array.isArray(gutter) ? gutter : [gutter, 0];
        normalizedGutter.forEach((g, index) => {
            if (typeof g === 'object') {
                for (let i = 0; i < responsiveArray.length; i++) {
                    const breakpoint = responsiveArray[i];
                    if (screens[breakpoint] && g[breakpoint] !== undefined) {
                        results[index] = g[breakpoint];
                        break;
                    }
                }
            }
            else {
                results[index] = g || 0;
            }
        });
        return results;
    };
    const prefixCls = 'teko-row';
    const gutters = getGutter();
    const classes = classNames(prefixCls, {
        [`${prefixCls}-no-wrap`]: wrap === false,
        [`${prefixCls}-${justify}`]: justify,
        [`${prefixCls}-${align}`]: align,
    }, className);
    const rowStyle = Object.assign(Object.assign(Object.assign({}, (gutters[0] && gutters[0] > 0
        ? {
            marginLeft: gutters[0] / -2,
            marginRight: gutters[0] / -2,
        }
        : {})), (gutters[1] && gutter[1] > 0
        ? {
            rowGap: gutters[1],
        }
        : {})), style);
    return (React__default.createElement(RowContext.Provider, { value: { gutter: gutters, wrap } },
        React__default.createElement(StyledRow, Object.assign({}, others, { className: classes, style: rowStyle, ref: ref }), children)));
});
Row.displayName = 'Row';
const StyledRow = styled.div `
  &.teko-row {
    display: flex;
    flex-flow: row wrap;
  }
  @media (min-width: ${BREAK_POINTS.lg}) {
    &.teko-row {
      justify-content: center;
    }
  }
  &.teko-row:before,
  &.teko-row:after {
    display: flex;
  }

  // No wrap of flex
  &.teko-row-no-wrap {
    flex-wrap: nowrap;
  }

  &.teko-row-start {
    justify-content: flex-start;
  }

  &.teko-row-center {
    justify-content: center;
  }

  &.teko-row-end {
    justify-content: flex-end;
  }

  &.teko-row-space-between {
    justify-content: space-between;
  }

  &.teko-row-space-around {
    justify-content: space-around;
  }

  &.teko-row-top {
    align-items: flex-start;
  }

  &.teko-row-middle {
    align-items: center;
  }

  &.teko-row-bottom {
    align-items: flex-end;
  }
  ${CommonStyles}
`;

export { Row };
